<template>
  <div id="gfLeftSideBar">
    <div v-show="!isOpenSetting" class="main-tab">
      <ul class="main-tab-item">
        <li
          v-for="(tab, ind) in tabs"
          :key="ind"
          class="tab-item"
          :class="{ active: tab.isActive }"
          @click="onSelectedTab(tab)">
          <a
            class="tab-label"
            :class="{
              disabled: !tab.isActive && previewPreset && linkedPresetId !== currentPreset.id,
            }">
            {{ tab.label }}
          </a>
        </li>
      </ul>
    </div>
    <EditorElements :is-show="isShowElementTab || !isShowGlobalStyleTab" />
    <GlobalStyle v-if="isShowGlobalStyleTab" />
    <div v-else class="variables-gs-color" v-html="variablesColorStylePresetInPage"></div>
    <img v-show="false" alt="" src="../../global-style/assets/images/PromotionOnboarding-1.gif" />
    <img v-show="false" alt="" src="../../global-style/assets/images/PromotionOnboarding-2.gif" />
    <img v-show="false" alt="" src="../../global-style/assets/images/PromotionOnboarding-3.gif" />
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import GlobalStyle from '@modules/global-style/components/GlobalStyle.vue';
import { mapGetters, mapState, mapActions, mapMutations } from 'vuex';
import { actions, mutations } from '@modules/editor-sidebar/store/types';
import store from '@/app/store/store';
import emitter from '@/emitter';
import { app } from '@/store';
import { mutations as mutaIframe } from '@/store/iframe/types';
import { cloneDeep } from '@/utils/common';
import EditorElements from './EditorElements.vue';
import { postMessageToParent } from '@/app/utils/helpers';

export default defineComponent({
  name: 'EditorSidebar',
  components: {
    GlobalStyle,
    EditorElements,
  },
  data: () => ({
    needCrawlLiveTheme: true,
    onbGlobalStyle: {},
    isRenderFirstPage: false,
  }),
  computed: {
    ...mapState('app', {
      shopInfo: 'shopInfo',
      pageInfo: 'pageInfo',
      shopMeta: 'shopMeta',
    }),
    ...mapState('sidebar', {
      tabs: 'tabs',
      linkedPresetId: 'linkedPresetId',
      presets: 'presets',
      currentPreset: 'currentPreset',
      currentPresetInPage: 'currentPresetInPage',
      variablesColorStylePresetInPage: 'variablesColorStylePresetInPage',
      previewPreset: 'previewPreset',
      isOpenSetting: 'isOpenSetting',
    }),
    ...mapGetters('sidebar', {
      presetIsEdited: 'PRESET_IS_EDITED',
    }),
    passwordEnabled() {
      return (this.shopInfo.passwordEnabled && this.shopInfo.password) || !this.shopInfo.passwordEnabled;
    },
    isShowElementTab() {
      return this.tabs.find((tab: any) => tab.isActive)?.key === 'elements';
    },
    isShowGlobalStyleTab() {
      return this.tabs.find((tab: any) => tab.isActive)?.key === 'global_style';
    },
    ...mapGetters('app', {
      isRunningAbTest: 'isRunningAbTest',
    }),
    sidePanelClass() {
      return this.isRunningAbTest ? 'ab-test__blur' : '';
    },
    sidePanelsWrapperClass() {
      return this.isRunningAbTest ? 'overflow-hidden' : '';
    },
    isSetCurrentPresetInPage() {
      return !(!this.linkedPresetId || !this.presets || !this.presets.length);
    },
  },
  watch: {
    pageInfo: {
      handler() {
        this.setCurrentPresetId();
      },
      deep: true,
    },
    isSetCurrentPresetInPage: {
      handler() {
        this.handleCurrentPresetInPage();
      },
      deep: true,
    },
    isShowElementTab: {
      handler() {
        const isSnippets = document.getElementById('md-snippets');

        if (isSnippets?.classList?.contains('active')) {
          this.setIsOpenSetting(false);
          return;
        }
        this.setIsOpenSetting(this.isShowElementTab);
      },
      deep: true,
    },
    presetIsEdited: {
      handler() {
        const isSaveDone = !this.presetIsEdited && store.getValue('__isSaved');
        postMessageToParent({ type: isSaveDone ? 'saveDone' : 'pageChanged' });
      },
      deep: true,
    },
  },
  async created() {
    this.$center.sidsebar = this;
    this.getBlockData();
    this.setCurrentPresetId();
    this.handleCurrentPresetInPage();
    this.setOpenFontManager(false);
    await this.getShopMeta();
    const shopMeta = this.shopMeta;
    (window as any).featurePromotionOB = null;
    if (!shopMeta || !shopMeta.firstCrawlPresetLiveTheme) {
      await this.crawlFromLiveTheme()
        .then((crawlData) => {
          if (crawlData && crawlData.status == 'success') {
            this.publicPreset({ preset: crawlData.data });

            const metaGlobalStyle = cloneDeep(this.shopMeta.onboardingGlobalStyle);
            (app as any).$store.state['app']['shopMeta']['firstCrawlPresetLiveTheme'] = true;

            if ((window as any).featurePromotionOB) {
              metaGlobalStyle['featurePromotion'] = true;
            }

            this.updateShopMeta({
              firstCrawlPresetLiveTheme: true,
              onboardingGlobalStyle: metaGlobalStyle,
            });
          }
        })
        .catch(() => {
          this.fetchPresets();
        });
    }
    await this.fetchPresets();
  },
  mounted() {
    setTimeout(() => {
      try {
        const isFromGlobalStyle = localStorage.getItem('from') === 'gs-create-preset';
        if (isFromGlobalStyle) {
          localStorage.setItem('from', '');
          this.onSelectedTab(this.tabs.find((tab: any) => tab.key === 'global_style'));
        }
      } catch (error) {}
    }, 600);
  },
  methods: {
    ...mapActions('sidebar', {
      fetchPresets: actions.FETCH_PRESETS,
      crawlFromLiveTheme: actions.CRAWL_FROM_LIVE_THEME,
      publicPreset: actions.PUBLIC_PRESET,
    }),
    ...mapActions('app', {
      getShopMeta: 'getShopMeta',
      updateShopMeta: 'updateShopMeta',
    }),
    ...mapMutations('sidebar', {
      changeActiveTab: mutations.CHANGE_ACTIVE_TAB,
      setCurrentPresetInPage: mutations.SET_CURRENT_PRESET_IN_PAGE,
      setLinkedPresetId: mutations.SET_LINKED_PRESET_ID,
      setIsOpenSetting: mutations.SET_IS_OPEN_SETTING,
    }),
    ...mapActions('font', {
      setOpenFontManager: 'openFontManager',
    }),
    ...mapMutations('iframe', {
      ALERT: mutaIframe.ALERT,
    }),
    ...mapActions('library', {
      getBlockData: 'getBlockData',
    }),
    countNumberChangeTabGlobalStyle() {
      if (this.shopMeta.onboardingGlobalStyle.previewPresetOB >= 2) {
        (app as any).$store.state['app']['shopMeta']['createNewPreset'] = true;
        return;
      }

      const payload = this.shopMeta;
      payload.onboardingGlobalStyle.previewPresetOB += 1;
      this.updateShopMeta(payload);
    },
    onSelectedTab(selectedTab: any) {
      if (this.previewPreset && this.linkedPresetId !== this.currentPreset.id) return;
      this.countNumberChangeTabGlobalStyle();

      const isElement = this.isShowGlobalStyleTab;
      if (isElement && this.presetIsEdited) {
        emitter.emit('WithoutPresetEdited', selectedTab);
      }

      if (!isElement || !this.presetIsEdited) {
        this.changeActiveTab(selectedTab);
      }

      emitter.on('CallBackWithoutPresetEdited', (tab: any) => {
        if (!tab && !tab?.isActive) return;
        this.changeActiveTab(tab);
      });
    },
    handleCurrentPresetInPage() {
      if (!this.linkedPresetId || !this.presets || !this.presets.length) {
        return;
      }
      const preset: any = this.presets.find((preset: any) => preset.id === this.linkedPresetId);
      if (!preset) return;
      if (!this.isRenderFirstPage) {
        this.setCurrentPresetInPage(preset);
        store.setValue('isRenderFirstPage', preset);
        this.isRenderFirstPage = true;
        return;
      }
    },
    setCurrentPresetId() {
      // eslint-disable-next-line camelcase
      if (this?.pageInfo?.page?.preset_id) {
        this.setLinkedPresetId(this.pageInfo.page.preset_id);
      }
    },
  },
});
</script>
